import { AfterViewInit, Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { Validators } from "@angular/forms";
import { ActivatedRoute, Params, Router } from "@angular/router";
import {
  AConnectionHandler,
  AdfsAuthentication,
  AuthenticationType,
  Connection,
  Dynamics365Connection,
  Dynamics365ConnectionHandlerService,
  GrantType,
  ManagedIdentityAuthentication,
  ManagedIdentityType,
  ModernAuthentication,
  PasswordAuthentication,
  SharePointConnection,
  SharePointConnectionHandlerService
} from "@coso/connection-forms";
import { Environment, ENVIRONMENT } from "@coso/environmenter";
import { FormBuilder, FormGroup, FormText, ViewMode } from "@coso/forms";
import { LoadingService } from "@coso/loading";
import { IS_SAAS, PRODUCT_NAME } from "@coso/utility";
import { WizardComponent as CosoWizardStepper } from "@coso/wizard";
import { StepIcon } from "@coso/wizard";
import { EMPTY, Observable, Subject, Subscription, throwError } from "rxjs";
import { catchError, filter, flatMap, switchMap, take, takeUntil, tap } from "rxjs/operators";
import { Guid } from "typescript-guid";
import { IConfigurationCreateOrUpdateDto, ISharePointConfigurationItem } from "../../models/auto-generated";
import { ConfigurationService } from "../../services/configuration.service";
import { ConnectionMapperService } from "../../services/connection-mapper.service";
import { Dynamics365ConnectionService } from "../../services/dynamics-365-connection.service";
import { EnvironmentService } from "../../services/environment.service";
import { ReplicationService } from "../../services/replication.service";
import { SettingsService } from "../../services/settings.service";
import { SharePointConnectionService } from "../../services/sharepoint-connection.service";
import { handleError } from "../../utils/handle-error";
import { ConfigurationWizardForm } from "./models/configuration-wizard-form";
import { IS_DOCKER } from "../../core.module";
import { resolveSharePointScope } from "../../utils/sharepoint-scope";
import { isValidUrl } from "../../utils/url-validator";

@Component({
  standalone:false,
  templateUrl: "./wizard.component.html",
  styleUrls: ["./wizard.component.scss"]
})
export class WizardComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly StepNotYetCompletedMissingRequiredFields = "This step is not yet completed, please fill all required fields, and test connection to continue";
  private readonly StepNotYetCompletedMissingSave = "This step is not yet completed, please wait until saving succeeds.";

  @ViewChild("organizationUrlPopover", { static: true }) organizationUrlPopover: TemplateRef<any>;
  private readonly _defaultAzureEndpoint = "https://login.microsoftonline.com/";
  private unsubscribe = new Subject<void>();
  private testConnectionSubscription: Subscription;
  ViewMode = ViewMode;
  instanceId: string;
  sharePointpApiPermission: string = "AllSites.FullControl";

  @ViewChild("wizard", { static: false }) wizard: CosoWizardStepper;
  formGroup: FormGroup<ConfigurationWizardForm>;
  stepIcons: StepIcon[];
  configurationFormText: FormText<IConfigurationCreateOrUpdateDto> = {
    name: {
      label: "Name",
      placeholder: "configuration name",
      popover: "The name is just a field which appears later in the list of configurations. By giving the configuration a meaningful name, it is easier to find it later in the list.",
      errors: {
        required: "The Name field is required.",
        maxlength: "The Name field must be at max 256 characters long."
      }
    },
    description: {
      label: "Description",
      placeholder: "description",
      popover: "A short description of the configuration. This field is optional.",
      errors: {
        maxlength: "The Name field must be at max 256 characters long."
      }
    }
  };
  d365ConnectionFormText: FormText<Dynamics365Connection>;

  get productShopUrl(): string {
    if (this.environment.production) {
      return "https://www.connecting-software.com/dynamics-crm-sharepoint-permissions-replicator-pricing/";
    }
    return "https://testshop.connecting-software.com/dynamics-crm-sharepoint-permissions-replicator-pricing/";
  }

  token: string;
  isActivationStepCompleted: boolean;
  activationSkipped: boolean;

  isSaveSuccessful: boolean;
  isConfigComplete: boolean;
  isTestingConnection: boolean;

  get nextButtonDisabled(): boolean {
    if (this.isTestingConnection) 
      return true;
    if (this.wizard) {
      if (this.isSaaS && this.wizard.selectedIndex === 3) {
        return !this.activationSkipped && !this.isActivationStepCompleted;
      }
      return this.wizard.selected.stepControl && this.wizard.selected.stepControl.invalid;
    }
    return true;
  }
  get prevButtonDisabled(): boolean {
    if (this.wizard && this.wizard.previouslySelected) {
      return !this.wizard.previouslySelected.editable;
    }
    return false;
  }

  constructor(
    public loadingService: LoadingService,
    @Inject(IS_DOCKER) public isDocker: boolean,
    @Inject(IS_SAAS) public isSaaS: boolean,
    @Inject(PRODUCT_NAME) public productName: string,
    @Inject(ENVIRONMENT) public environment: Environment,
    public dynamics365ConnectionHandler: Dynamics365ConnectionHandlerService,
    public sharePointConnectionHandler: SharePointConnectionHandlerService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private configurationService: ConfigurationService,
    private dynamics365ConnectionService: Dynamics365ConnectionService,
    private sharePointConnectionService: SharePointConnectionService,
    private settingsService: SettingsService,
    private connectionMapperService: ConnectionMapperService,
    private replicationService: ReplicationService,
    private environmentService: EnvironmentService
  ) {}

  ngAfterViewInit(): void {
    if (this.isSaaS === false) {
      // more info: https://angular.io/errors/NG0100
      setTimeout(() => {
        this.wizard.showStepWarning(
          `Keep in mind that after you click Next, your configuration will be saved. If you quit the wizard before completing all the steps, you might end up with an incomplete or inconsistent configuration.`
        );
      }, 0);
    }
  }

  ngOnInit(): void {
    this.generateStepIcons();
    this.generateFormGroup();
    if (this.isSaaS) {
      this.route.queryParams.subscribe((params: Params) => (this.token = params["token"]));
    }
    this.environmentService.getDefaultClientId().subscribe((defaultClientId) => {
      this.formGroup.controls.dynamics365.controls.modernAuthentication.controls.clientId.setValue(defaultClientId);
      this.formGroup.controls.sharePoint.controls.modernAuthentication.controls.clientId.setValue(defaultClientId);
    });
    sessionStorage.setItem("instance-id", this.formGroup.controls.configuration.controls.id.value);
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    // when a user quits the wizard before completing all the configuration steps, the configuration should be deleted
    if (this.instanceId && !this.isConfigComplete) {
      this.deleteConfiguration$().subscribe();
    }
    sessionStorage.removeItem("instance-id");
  }

  onNextButtonClick(next: () => void) {
    switch (this.wizard.selectedIndex) {
      case 0:
        this.goToSecondStep(next);
        break;
      case 1:
        this.goToThirdStep(next);
        break;
      case 2:
        this.goToFourthStep(next);
        break;
      default:
        next();
        break;
    }
  }

  onPreviousButtonClick(previous: () => void) {
    previous();
  }

  onWizardFinished() {
    const startReplication = this.formGroup.getRawValue().startReplication;
    if (startReplication === true) {
      this.loadingService.start(`Starting replication process`, "startReplication");
      this.replicationService.start(this.instanceId).subscribe(
        () => {
          this.router.navigate(this.isSaaS ? ["../"] : ["../", this.instanceId, "logs", "real-time-logs"], { relativeTo: this.route.parent });
          this.loadingService.stop("startReplication");
        },
        (error) => {
          this.wizard.selected.hasError = true;
          this.wizard.selected.errorMessage = `Failed to start replication due to: ${handleError(error)}`;
          this.loadingService.stop("startReplication");
        }
      );
    } else {
      this.router.navigate(["../"], { relativeTo: this.route.parent });
    }
  }

  private goToSecondStep(next: () => void) {
    this.formGroup.controls.configuration.markAllAsTouched();
    if (this.formGroup.controls.configuration.invalid) {
      this.wizard.showStepWarning("This step is not yet completed, please fill all required fields.");
    } else {
      if (this.isSaaS) {
        next();
      } else {
        this.wizard.showStepWarning(this.StepNotYetCompletedMissingSave);
        this.save(next);
      }
    }
  }

  private goToThirdStep(next: () => void) {
    if (this.formGroup.controls.dynamics365.invalid) {
      this.wizard.showStepWarning(this.StepNotYetCompletedMissingRequiredFields);
    } else {
      const dynamics365Connection = this.formGroup.controls.dynamics365.getRawValue();
      this.goToNextStepIfConnectionIsValid(this.dynamics365ConnectionHandler, dynamics365Connection, () => {
        this.wizard.steps.toArray()[this.wizard.selectedIndex].completed = true;
        this.updateSharePointFormData();
        // This warning message will be dispayed in the third step.
        this.wizard.showStepWarning(`Keep in mind that after you click Next, ${this.isSaaS ? "your configuration will be saved" : "your connections will be saved"}.`);
        next();
      });
    }
  }

  private goToFourthStep(next: () => void) {
    if (this.formGroup.controls.sharePoint.invalid) {
      this.wizard.showStepWarning(this.StepNotYetCompletedMissingRequiredFields);
    } else {
      const sharePointConnection = this.formGroup.controls.sharePoint.getRawValue();
      this.goToNextStepIfConnectionIsValid(this.sharePointConnectionHandler, sharePointConnection, () => {
        this.wizard.showStepWarning(this.StepNotYetCompletedMissingSave);
        this.save(next);
      });
    }
  }

  private goToNextStepIfConnectionIsValid(handler: AConnectionHandler, connection: Connection, next: () => void) {
    if (this.testConnectionSubscription) {
      this.testConnectionSubscription.unsubscribe();
    }

    handler.isTestingConnectionFor$(connection.internalId).subscribe((r) => {
      this.isTestingConnection = r;
    });

    this.testConnectionSubscription = handler
      .isTestConnectionSuccessfulFor$(connection.internalId)
      .pipe(
        take(1),
        switchMap((isSuccess) => {
          if (isSuccess) {
            next();
            return EMPTY;
          } else {
            this.wizard.showStepWarning(this.StepNotYetCompletedMissingRequiredFields);
            handler.testConnection(connection);

            return handler.testConnectionResultFor$(connection.internalId).pipe(
              filter((r) => !!r && r.isSuccess),
              take(1),
              tap(() => next())
            );
          }
        })
      )
      .subscribe();
  }

  private generateStepIcons(): void {
    this.stepIcons = [];
    this.stepIcons.push({ kind: 1, identifier: "fa-magic" });
    this.stepIcons.push({
      kind: 0,
      identifier:
        "data:image/png;base64,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",
      accessibilityLabel: "Dynamics 365 Logo"
    });
    this.stepIcons.push({
      kind: 0,
      identifier:
        "data:image/png;base64,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",
      accessibilityLabel: "SharePoint Logo"
    });
    if (this.isSaaS === true) {
      this.stepIcons.push({ kind: 1, identifier: "fa-credit-card-alt" });
    }
    this.stepIcons.push({ kind: 1, identifier: "fa-check" });
  }

  private generateFormGroup(): void {
    const id = Guid.create().toString();
    this.formGroup = this.fb.group<ConfigurationWizardForm>({
      startReplication: this.fb.control(false),
      dynamics365: this.fb.group<Dynamics365Connection>({
        id: this.fb.control(null),
        organizationUrl: this.fb.control(null),
        authenticationType: this.fb.control(AuthenticationType.Modern),
        modernAuthentication: this.fb.group<ModernAuthentication>({
          tenantId: this.fb.control(null),
          azureEndpoint: this.fb.control(this._defaultAzureEndpoint),
          clientId: this.fb.control(null),
          certificateThumbprint: this.fb.control(null),
          useDefaultCertificate: this.fb.control(true, [Validators.required]),
          grantType: this.fb.control(GrantType.ClientCredentials),
          clientSecret: this.fb.control(null),
          encryptedToken: this.fb.control(null),
          scope: this.fb.control("offline_access"),
          changeAzureAdApp: this.fb.control(false),
          useDefaultAzureAdApp: this.fb.control(true),
          saasConsentCallbackGrantScope: this.fb.control(id),
          apiPermission: this.fb.control(null),
        }),
        passwordAuthentication: this.fb.group<PasswordAuthentication>({
          username: this.fb.control(null),
          password: this.fb.control(null),
          changePassword: this.fb.control(false)
        }),
        managedIdentityAuthentication: this.fb.group<ManagedIdentityAuthentication>({
          type: this.fb.control(ManagedIdentityType.SystemAssigned),
          clientId: this.fb.control(null)
        })
      }),
      sharePoint: this.fb.group<SharePointConnection>({
        id: this.fb.control(null),
        siteCollectionUrl: this.fb.control(null),
        isOnline: this.fb.control(null),
        authenticationType: this.fb.control(AuthenticationType.Modern),
        modernAuthentication: this.fb.group<ModernAuthentication>({
          tenantId: this.fb.control(null),
          azureEndpoint: this.fb.control(this._defaultAzureEndpoint),
          clientId: this.fb.control(null),
          certificateThumbprint: this.fb.control(null),
          useDefaultCertificate: this.fb.control(true, [Validators.required]),
          grantType: this.fb.control(GrantType.ClientCredentials),
          clientSecret: this.fb.control(null),
          encryptedToken: this.fb.control(null),
          scope: this.fb.control("offline_access"),
          changeAzureAdApp: this.fb.control(false),
          useDefaultAzureAdApp: this.fb.control(true),
          saasConsentCallbackGrantScope: this.fb.control(id),
          apiPermission: this.fb.control<string>("AllSites.FullControl", [Validators.required]),
        }),
        passwordAuthentication: this.fb.group<PasswordAuthentication>({
          username: this.fb.control(null),
          password: this.fb.control(null),
          changePassword: this.fb.control(null)
        }),
        adfsAuthentication: this.fb.group<AdfsAuthentication>({
          adfsUrl: this.fb.control(null),
          realmUrn: this.fb.control(null),
          passwordAuthentication: this.fb.group<PasswordAuthentication>({
            username: this.fb.control(null),
            password: this.fb.control(null),
            changePassword: this.fb.control(null)
          })
        }),
        managedIdentityAuthentication: this.fb.group<ManagedIdentityAuthentication>({
          type: this.fb.control(ManagedIdentityType.SystemAssigned),
          clientId: this.fb.control(null)
        })
      }),
      configuration: this.fb.group<IConfigurationCreateOrUpdateDto>({
        id: this.fb.control(id),
        name: this.fb.control(undefined, [Validators.required, Validators.maxLength(256)]),
        description: this.fb.control(undefined, [Validators.maxLength(256)])
      })
    });
    this.formGroup.controls.dynamics365.controls.organizationUrl.valueChanges
      .pipe(
        takeUntil(this.unsubscribe),
        filter((u) => !!u)
      )
      .subscribe((value) => {
        if (this.formGroup.controls.dynamics365.controls.organizationUrl.valid) {
          const scope = `offline_access ${value.replace(/\/$/, "")}/user_impersonation`;
          this.formGroup.controls.dynamics365.controls.modernAuthentication.controls.scope.setValue(scope);
        }
      });
    this.formGroup.controls.sharePoint.controls.siteCollectionUrl.valueChanges
      .pipe(
        takeUntil(this.unsubscribe),
        filter((u) => !!u)
      )
      .subscribe((value) => {
        if (this.formGroup.controls.sharePoint.controls.siteCollectionUrl.valid) {
          this.setScope(value);
        }
      });
    this.formGroup.controls.sharePoint.controls.authenticationType.valueChanges
      .pipe(
        takeUntil(this.unsubscribe),
        filter((u) => u !== AuthenticationType.Adfs)
      )
      .subscribe(() => this.formGroup.controls.sharePoint.controls.adfsAuthentication.reset());

    this.formGroup.controls.sharePoint.controls.modernAuthentication?.controls.apiPermission?.valueChanges
      .pipe(
        takeUntil(this.unsubscribe),
        filter((u) => !!u)
      )
      .subscribe((value) => {
        this.sharePointpApiPermission = value;
        this.setScope(this.formGroup.controls.sharePoint.controls.siteCollectionUrl.value);
      });

    this.d365ConnectionFormText = {
      organizationUrl: { popover: this.organizationUrlPopover }
    };
  }

  private setScope(url: string) {
    if (isValidUrl(url)){
      this.formGroup.controls.sharePoint.controls.modernAuthentication.controls.scope.setValue(resolveSharePointScope(url, this.sharePointpApiPermission));
    }
  }

  private updateSharePointFormData() {
    this.formGroup.controls.sharePoint.controls.authenticationType.setValue(this.formGroup.controls.dynamics365.controls.authenticationType.value);
    this.formGroup.controls.sharePoint.controls.passwordAuthentication.controls.username.setValue(this.formGroup.controls.dynamics365.controls.passwordAuthentication.controls.username.value);
    this.formGroup.controls.sharePoint.controls.passwordAuthentication.controls.password.setValue(this.formGroup.controls.dynamics365.controls.passwordAuthentication.controls.password.value);
    this.formGroup.controls.sharePoint.controls.modernAuthentication.controls.azureEndpoint.setValue(this.formGroup.controls.dynamics365.controls.modernAuthentication.controls.azureEndpoint.value);
    this.formGroup.controls.sharePoint.controls.modernAuthentication.controls.grantType.setValue(this.formGroup.controls.dynamics365.controls.modernAuthentication.controls.grantType.value);
    this.formGroup.controls.sharePoint.controls.modernAuthentication.controls.clientId.setValue(this.formGroup.controls.dynamics365.controls.modernAuthentication.controls.clientId.value);
    this.formGroup.controls.sharePoint.controls.modernAuthentication.controls.tenantId.setValue(this.formGroup.controls.dynamics365.controls.modernAuthentication.controls.tenantId.value);
    this.formGroup.controls.sharePoint.controls.modernAuthentication.controls.useDefaultCertificate.setValue(
      this.formGroup.controls.dynamics365.controls.modernAuthentication.controls.useDefaultCertificate.value
    );
    this.formGroup.controls.sharePoint.controls.modernAuthentication.controls.certificateThumbprint.setValue(
      this.formGroup.controls.dynamics365.controls.modernAuthentication.controls.certificateThumbprint.value
    );
    this.formGroup.controls.sharePoint.controls.modernAuthentication.controls.clientSecret.setValue(this.formGroup.controls.dynamics365.controls.modernAuthentication.controls.clientSecret.value);
    this.formGroup.controls.sharePoint.controls.managedIdentityAuthentication.controls.type.setValue(this.formGroup.controls.dynamics365.controls.managedIdentityAuthentication.controls.type.value);
    this.formGroup.controls.sharePoint.controls.managedIdentityAuthentication.controls.clientId.setValue(
      this.formGroup.controls.dynamics365.controls.managedIdentityAuthentication.controls.clientId.value
    );
  }

  private save(next: () => void) {
    this.loadingService.start("Saving configuration", "save");
    this.saveConfiguration$().subscribe(
      () => {
        this.wizard.steps.toArray()[this.wizard.selectedIndex].completed = true;
        this.wizard.closeStepWarning();
        for (let i = this.wizard.selectedIndex; i >= 0; i--) {
          const prevStep = this.wizard.steps.find((_item, index, _array) => index === i);
          prevStep.editable = false;
        }
        next();
        this.loadingService.stop("save");
      },
      (error) => {
        this.wizard.steps.toArray()[this.wizard.selectedIndex].completed = false;
        this.wizard.selected.hasError = true;
        this.wizard.selected.errorMessage = error;
        this.loadingService.stop("save");
      }
    );
  }

  private createDynamics365Connection$(): Observable<any> {
    const connection = this.connectionMapperService.mapFromDynamics365Connection(this.formGroup.getRawValue().dynamics365);
    return this.dynamics365ConnectionService
      .upsertConnection(this.instanceId, connection)
      .pipe(catchError((error) => throwError(`Failed to create Dynamics 365 connection due to: ${handleError(error)}`)));
  }

  private createSharePointConnection$(): Observable<any> {
    const connection = this.connectionMapperService.mapFromSharePointConnection(this.formGroup.getRawValue().sharePoint);
    return this.settingsService.get(this.instanceId).pipe(
      flatMap((data) => {
        if (data.sharePoint.sharepointConfig.length > 0) {
          const connectionId = data.sharePoint.sharepointConfig[0].key;
          return this.sharePointConnectionService.updateConnection(this.instanceId, connectionId, connection);
        } else {
          return this.sharePointConnectionService.createConnection(this.instanceId, "Default").pipe(
            flatMap((item: ISharePointConfigurationItem) => {
              return this.sharePointConnectionService.updateConnection(this.instanceId, item.key, connection);
            })
          );
        }
      }),
      catchError((error) => throwError(`Failed to create SharePoint connection due to: ${handleError(error)}`))
    );
  }

  private upsertConfiguration$(): Observable<any> {
    const configuration = this.formGroup.getRawValue().configuration;
    if (this.instanceId) {
      return this.configurationService.update(this.instanceId, configuration).pipe(
        tap(() => (this.instanceId = configuration.id)),
        catchError((error) => throwError(`Failed to update configuration due to: ${handleError(error)}`))
      );
    } else {
      return this.configurationService.create(configuration).pipe(
        tap(() => (this.instanceId = configuration.id)),
        catchError((error) => throwError(`Failed to create configuration due to: ${handleError(error)}`))
      );
    }
  }

  private saveConfiguration$(): Observable<any> {
    if (this.wizard.selectedIndex === 2) {
      if (this.isSaaS) {
        return this.upsertConfiguration$().pipe(
          flatMap(() => this.createDynamics365Connection$()),
          flatMap(() => this.createSharePointConnection$()),
          tap(() => (this.isConfigComplete = true))
        );
      } else {
        return this.createDynamics365Connection$().pipe(
          flatMap(() => this.createSharePointConnection$()),
          tap(() => (this.isConfigComplete = true))
        );
      }
    } else {
      return this.upsertConfiguration$();
    }
  }

  private deleteConfiguration$(): Observable<any> {
    return this.configurationService
      .delete(this.instanceId)
      .pipe(catchError((error) => throwError(`You might have incomplete or inconsistent configuration! Failed to delete configuration due to: ${handleError(error)}`)));
  }
}
